import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { WINDOW } from './window.service';

import { httpRetry } from '@core/utils/httpRetry';
import { Booking } from '@generated/openapi/model/booking';
import { BookingCreation } from '@generated/openapi/model/bookingCreation';
import { CheckoutBillingDetails } from '@generated/openapi/model/checkoutBillingDetails';
import { CheckoutPayment } from '@generated/openapi/model/checkoutPayment';
import { CheckoutPaymentOption } from '@generated/openapi/model/checkoutPaymentOption';
import { Client } from '@generated/openapi/model/client';
import { CustomDeparture } from '@generated/openapi/model/customDeparture';
import { GroupDeparture } from '@generated/openapi/model/groupDeparture';
import { Invoice } from '@generated/openapi/model/invoice';
import { InvoiceStats } from '@generated/openapi/model/invoiceStats';
import { LegacyContactLeadRequest } from '@generated/openapi/model/legacyContactLeadRequest';
import { LegacyLeadMagnet } from '@generated/openapi/model/legacyLeadMagnet';
import { LegacyTripCustomDepartureRequest } from '@generated/openapi/model/legacyTripCustomDepartureRequest';
import { LineItem } from '@generated/openapi/model/lineItem';
import { LineItemsCreation } from '@generated/openapi/model/lineItemsCreation';
import { Lock } from '@generated/openapi/model/lock';
import { MergedPage } from '@generated/openapi/model/mergedPage';
import { Payment } from '@generated/openapi/model/payment';
import { PaymentCandidate } from '@generated/openapi/model/paymentCandidate';
import { PaymentCreation } from '@generated/openapi/model/paymentCreation';
import { PaymentError } from '@generated/openapi/model/paymentError';
import { PublicDeparture } from '@generated/openapi/model/publicDeparture';
import { SalesPerson } from '@generated/openapi/model/salesPerson';
import { StripePaymentIntent } from '@generated/openapi/model/stripePaymentIntent';
import { TripManager } from '@generated/openapi/model/tripManager';
import { UserDetails } from '@generated/openapi/model/userDetails';
import { Version } from '@generated/openapi/model/version';
import { concatMap, map, tap } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { EnvironmentService } from './environment.service';

export enum Routes {
    register = '/api/dj-rest-auth/register/',
    login = '/api/dj-rest-auth/login/',
    logout = '/api/dj-rest-auth/logout/',
    refresh = '/api/dj-rest-auth/token/refresh/',
    user = '/api/dj-rest-auth/user/',
    requestPasswordReset = '/api/dj-rest-auth/password/reset/',
    confirmPasswordReset = '/api/dj-rest-auth/password/reset/confirm/',
    profilePicture = '/api/users/upload_picture/',
    resendVerificationEmail = '/api/users/resend_verification_email/',
    referrals = '/api/referrals/',
    profile = '/api/users/profile/',
    country = '/api/users/country/',
    email = '/api/users/email/',
    trips = '/api/trips/',
    voTrips = '/api/trips/virtual_office/',
    itineraries = '/api/trips/itineraries/',
    voDepartures = '/api/departures/departures/',
    publicDepartures = '/api/departures/public_departures/',
    legacyDepartures = '/api/trips/departures/',
    favourites = '/api/trips/favourites/',
    countReferral = '/api/stats/count_landing/',
    countNotFound = '/api/stats/count_not_found/',
    seoMetadata = '/api/seo/metadata/',
    joinNewsletter = '/api/newsletter/newsletter/join/',
    leadMagnet = '/api/newsletter/lead_magnet/',
    contact = '/api/contact/contact/contact/',
    invoices = '/api/invoices/invoices/',
    publicInvoices = '/api/invoices/public_invoices/',
    bookings = '/api/bookings/bookings/',
    invoiceClients = '/api/invoices/clients/',
    clients = '/api/clients/clients/',
    tripManagers = '/api/invoices/trip_managers/',
    lineItems = '/api/invoices/line_items/',
    invoicesStats = '/api/invoices/stats/',
    products = '/api/products/products/',
    productVersions = '/api/products/versions/',
    payments = '/api/payments/payments/',
    paymentsPoll = '/api/payments/poll/',
    paymentReminders = '/api/payments/reminders/',
    healthAndSafetyReminders = '/api/health_and_safety/reminders/',
    googleFormSubmissions = '/api/health_and_safety/google_form_submissions/',
    transfers = '/api/payments/transfers/',
    locks = '/api/locks/locks/',
    contacts = '/api/active_campaign/contacts/',
    version = '/api/stats/version/',
    paymentEmailLog = '/api/emails/payments/',
    bookingEmailLog = '/api/emails/bookings/',
    webVitals = '/api/stats/web_vitals/',
    config = '/api/config/config/',
    checkout = '/api/checkout/',
    checkoutSummary = '/api/checkout/summary/',
    checkoutReservation = '/api/checkout/reservation/',
    checkoutPayment = '/api/checkout/payment/',
    salesPeople = '/api/invoices/sales_people/',
    feedbackForms = '/api/feedback/forms/',
    feedbackReminders = '/api/feedback/reminders/',
    feedbackEmailLog = '/api/emails/feedback/',
    emailLog = '/api/emails/log/',
    destinations = '/api/destinations/destinations/',
    paymentError = '/api/stats/payment_error/',
    leads = '/api/leads/lead/',
    itineraryTemplates = '/api/itinerary_templates/itinerary_templates/',
    accommodations = '/api/accommodations/accommodations/',
}

export interface PaginatedResponse<T> {
    count?: number;
    next?: string | null;
    previous?: string | null;
    page?: number | null;
    total_pages?: number | null;
    results?: Array<T>;
}

export interface DRFFilter {
    [key: string]: string | number | boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DjangoService {
    readonly cacheNamespace = 'DjangoService';

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        private cacheService: CacheService,
        private environmentService: EnvironmentService,
        @Inject(WINDOW) private window: Window
    ) {}

    _url(route, resource: string | number = null, action: string = null) {
        const protocol = '';
        const url_action = action ? action + '/' : '';
        const url_resource = resource ? resource + '/' + url_action : '';
        const hostname = this.environmentService.hostName();
        return protocol + hostname + route + url_resource;
    }

    register(data) {
        return this.http.post(this._url(Routes.register), data, {
            headers: { skip: 'true' },
        });
    }

    login(data) {
        return this.http
            .post(this._url(Routes.login), data, {
                headers: { skip: 'true' },
            })
            .pipe(httpRetry());
    }

    logout() {
        return this.http
            .post(this._url(Routes.logout), {}, { headers: { skip: 'true' } })
            .pipe(
                httpRetry(),
                tap(() => this.clear_cache())
            );
    }

    refresh(data) {
        return this.http
            .post(this._url(Routes.refresh), data, {
                headers: { skip: 'true' },
            })
            .pipe(httpRetry());
    }

    user(): Observable<UserDetails> {
        const route = Routes.user;
        const observable = this.http
            .get<UserDetails>(this._url(route))
            .pipe(httpRetry());
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            route,
            this.environmentService.isBrowser(),
            observable,
            30 * 1000
        );
    }

    uploadProfilePicture(data) {
        return this.http.put(this._url(Routes.profilePicture), data);
    }

    referrals() {
        const route = Routes.referrals;
        const observable = this.http.get(this._url(route)).pipe(httpRetry());

        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            route,
            this.environmentService.isBrowser(),
            observable,
            60 * 1000
        );
    }

    profile(referralCode: string) {
        const route = Routes.profile;
        const observable = this.http
            .get(this._url(route, referralCode))
            .pipe(httpRetry());
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            route,
            this.environmentService.isBrowser(),
            observable,
            60 * 1000
        );
    }

    updateUser(data: any) {
        const route = Routes.user;
        return this.http.patch(this._url(route), data);
    }

    requestPasswordReset(data: { email }) {
        const route = Routes.requestPasswordReset;
        return this.http.post(this._url(route), data);
    }

    confirmPasswordReset(data: { uid; token; new_password1; new_password2 }) {
        const route = Routes.confirmPasswordReset;
        return this.http.post(this._url(route), data);
    }

    country() {
        const route = Routes.country;
        const observable = this.http.get<{ country: string }>(this._url(route));
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            route,
            true,
            observable,
            60 * 1000
        );
    }

    emailExists(email: string) {
        const route = Routes.email;
        return this.http.get(this._url(route), { params: { email: email } });
    }

    resendVerificationEmail() {
        const route = Routes.resendVerificationEmail;
        return this.http.post(this._url(route), {});
    }

    countReferralCode(data: { referral_code: string }) {
        const route = Routes.countReferral;
        return this.http.post(this._url(route), data);
    }

    countNotFound(data: { path: string }) {
        const route = Routes.countNotFound;
        return this.http.post(this._url(route), data);
    }

    tripCustomDeparture(
        url_slug: string,
        data: LegacyTripCustomDepartureRequest
    ) {
        const route = Routes.trips;
        const resource = `${url_slug}/custom_departure`;
        return this.http
            .post(this._url(route, resource), data)
            .pipe(httpRetry());
    }

    seoMetadata(path: string): Observable<MergedPage> {
        const route = Routes.seoMetadata;
        const observable = this.http
            .get<MergedPage>(this._url(route, path))
            .pipe(httpRetry());
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            route + path,
            true,
            observable,
            60 * 60 * 1000
        );
    }

    joinNewsletter(data: { email: string; first_name: string }) {
        const route = Routes.joinNewsletter;
        return this.http.post(this._url(route), data).pipe(httpRetry());
    }

    contact(data: LegacyContactLeadRequest) {
        const route = Routes.contact;
        return this.http.post(this._url(route), data).pipe(httpRetry());
    }

    leadMagnet(data: LegacyLeadMagnet) {
        const route = Routes.leadMagnet;
        return this.http.post(this._url(route), data).pipe(httpRetry());
    }

    updateInvoice(
        invoiceId: number,
        invoice: Partial<Invoice>
    ): Observable<Invoice> {
        const route = Routes.invoices;
        return this.http.patch(
            this._url(route, invoiceId.toString()),
            invoice
        ) as Observable<Invoice>;
    }

    sendInvoiceCreatedEmail(invoiceId: number) {
        const route = Routes.invoices;
        return this.http.post(
            this._url(route, `${invoiceId}/send_created_email`),
            {}
        ) as Observable<any>;
    }

    sendInvoiceUpdatedEmail(invoiceId: number) {
        const route = Routes.invoices;
        return this.http.post(
            this._url(route, `${invoiceId}/send_updated_email`),
            {}
        ) as Observable<any>;
    }
    searchContacts(search: string): Observable<Client[]> {
        const route = Routes.contacts;
        const params = {
            email: search,
        };
        return this.http
            .get<Client[]>(this._url(route), { params: params })
            .pipe(httpRetry());
    }

    searchClients(search: string): Observable<PaginatedResponse<Client>> {
        const route = Routes.invoiceClients;
        const params = {
            search: search,
        };
        return this.http
            .get<PaginatedResponse<Client>>(this._url(route), {
                params: params,
            })
            .pipe(httpRetry());
    }
    getClient(email: string): Observable<Client> {
        const route = Routes.invoiceClients;
        return this.http.get<Client>(this._url(route, email)).pipe(httpRetry());
    }
    createClient(client: Client): Observable<Client> {
        const route = Routes.invoiceClients;
        return this.http.post(this._url(route), client) as Observable<Client>;
    }
    updateClient(client: Client): Observable<Client> {
        const route = Routes.invoiceClients;
        return this.http.put(
            this._url(route, client.id),
            client
        ) as Observable<Client>;
    }

    tripManagers(): Observable<TripManager[]> {
        const route = Routes.tripManagers;
        return this.http.get<TripManager[]>(this._url(route)).pipe(httpRetry());
    }

    salesPeople(): Observable<SalesPerson[]> {
        return this.http
            .get<SalesPerson[]>(this._url(Routes.salesPeople))
            .pipe(httpRetry());
    }

    payments(
        invoiceId: number,
        name?: Payment.NameEnum,
        isEditable?: boolean
    ): Observable<Payment[]> {
        const route = Routes.payments;
        const params = {
            invoice_id: invoiceId,
            ...(name && { name: name }),
            ...(isEditable !== undefined && { is_editable: isEditable }),
        };

        const observable = this.http
            .get<Payment[]>(this._url(route), { params: params })
            .pipe(httpRetry());

        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            `${route}${invoiceId}${name}${isEditable}`,
            true,
            observable,
            1000
        );
    }

    postPaymentError(error: PaymentError) {
        const route = Routes.paymentError;
        return this.http.post(this._url(route), error);
    }

    retrieveCheckoutPayment(paymentId: string) {
        const route = Routes.checkoutPayment;
        return this.drfGet<CheckoutPayment>(route, paymentId).pipe(httpRetry());
    }

    updateStatusCheckoutPayment(paymentId: string) {
        const route = Routes.checkoutPayment;
        const resource = `${paymentId}/update_status`;
        return this.http.post<CheckoutPayment>(this._url(route, resource), {});
    }

    updatePayments(payments: PaymentCreation) {
        const route = Routes.payments;
        return this.http.post<Payment>(this._url(route), payments);
    }

    updateBookings(bookings: BookingCreation) {
        const route = Routes.bookings;
        return this.http.post<Booking>(this._url(route), bookings);
    }

    updateLineItems(lineItems: LineItemsCreation) {
        const route = Routes.lineItems;
        return this.http.post<LineItem>(this._url(route), lineItems);
    }

    duePayments(): Observable<Payment[]> {
        const route = Routes.payments;
        return this.http
            .get<Payment[]>(this._url(route, 'due'))
            .pipe(httpRetry());
    }

    unlockPayment(paymentId: number): Observable<Payment> {
        const route = Routes.payments;
        const resource = `${paymentId}/unlock`;
        return this.http.post(
            this._url(route, resource),
            {}
        ) as Observable<Payment>;
    }

    reducePaymentAmount(paymentId: number): Observable<Payment> {
        const route = Routes.payments;
        const resource = `${paymentId}/reduce_amount`;
        return this.http.post(
            this._url(route, resource),
            {}
        ) as Observable<Payment>;
    }

    cancelDeparture(
        departureId: string
    ): Observable<PublicDeparture | CustomDeparture | GroupDeparture> {
        const route = Routes.voDepartures;
        const resource = `${departureId}/cancel`;
        return this.http.post(this._url(route, resource), {}) as Observable<
            PublicDeparture | CustomDeparture | GroupDeparture
        >;
    }

    restoreDeparture(
        departureId: string
    ): Observable<PublicDeparture | CustomDeparture | GroupDeparture> {
        const route = Routes.voDepartures;
        const resource = `${departureId}/restore`;
        return this.http.post(this._url(route, resource), {}) as Observable<
            PublicDeparture | CustomDeparture | GroupDeparture
        >;
    }

    releaseReservation(reservationId: string) {
        const route = Routes.checkoutReservation;
        const resource = `${reservationId}/release`;
        return this.http.post(this._url(route, resource), {});
    }

    paymentOptions(paymentId: string): Observable<CheckoutPaymentOption[]> {
        const route = Routes.checkoutPayment;
        const resource = `${paymentId}/payment_options`;
        return this.http.get<CheckoutPaymentOption[]>(
            this._url(route, resource)
        );
    }

    getPaymentIntent(
        paymentId: string,
        type: CheckoutPaymentOption.TypeEnum
    ): Observable<StripePaymentIntent> {
        const route = Routes.checkoutPayment;
        const resource = `${paymentId}/payment_intent`;
        return this.http.post<StripePaymentIntent>(this._url(route, resource), {
            type,
        });
    }

    addBillingDetailsForReservation(
        reservationId: string,
        data: CheckoutBillingDetails
    ): Observable<CheckoutBillingDetails> {
        const route = Routes.checkoutReservation;
        const resource = `${reservationId}/add_billing_details`;
        return this.http.post(
            this._url(route, resource),
            data
        ) as Observable<CheckoutBillingDetails>;
    }

    paymentCandidates(
        bankTransferId: string,
        page: number = 1,
        pageSize: number = 100,
        order: string = null,
        filters: DRFFilter = {}
    ): Observable<PaginatedResponse<PaymentCandidate>> {
        const route = Routes.transfers;
        const resource = `${bankTransferId}/bank_transfer_payment_candidates`;
        const params = {
            page: page,
            page_size: pageSize,
            ordering: order,
            ...filters,
        };
        return this.http
            .get(this._url(route, resource), { params: params })
            .pipe(httpRetry());
    }

    /**
     *  * Fetches a list of items without pagination from the Django REST Framework API.
     * @param {Routes | string} route either route from the Routes enum or a custom string
     * @param {string} order optional ordering string
     * @param {DRFFilter} filters optional filters to apply to the request
     * @param {boolean} useCache whether to use the cache or not
     * @param {number} cacheDuration duration of the cache in milliseconds. only used if useCache is true
     */
    drfList<T>(
        route: Routes | string,
        order: string = null,
        filters: DRFFilter = {},
        useCache: boolean = false,
        cacheDuration: number = 1000
    ): Observable<T[]> {
        const params = {
            ordering: order,
            ...filters,
        };
        const key = `${route}${JSON.stringify(params)}`;
        const result = this.http
            .get<T[]>(this._url(route), { params: params })
            .pipe(httpRetry());
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            key,
            useCache,
            result,
            cacheDuration
        );
    }

    /**
     * Fetches a paginated list of items from the Django REST Framework API.
     * @param {Routes | string} route either route from the Routes enum or a custom string
     * @param {number} page page number to fetch
     * @param {number} pageSize number of items per page
     * @param {string} order optional ordering string
     * @param {DRFFilter} filters optional filters to apply to the request
     * @param {boolean} useCache whether to use the cache or not
     * @param {number} cacheDuration duration of the cache in milliseconds. only used if useCache is true
     * @returns {PaginatedResponse<T>} an observable of the paginated response
     */
    drfListPaginated<T>(
        route: Routes | string,
        page: number = 1,
        pageSize: number = 10,
        order: string = null,
        filters: DRFFilter = {},
        useCache: boolean = false,
        cacheDuration: number = 1000
    ): Observable<PaginatedResponse<T>> {
        const params = {
            ...filters,
            page: page,
            page_size: pageSize,
            ordering: order,
        };

        const key = `${route}${JSON.stringify(params)}`;
        const result = this.http
            .get<PaginatedResponse<T>>(this._url(route), { params: params })
            .pipe(httpRetry());
        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            key,
            useCache,
            result,
            cacheDuration
        );
    }

    /**
     * Fetches all results from a paginated list of items from the Django REST Framework API by recursively iterating over pages.
     * @param {Routes | string} route either route from the Routes enum or a custom string
     * @param {string} order optional ordering string
     * @param {DRFFilter} filters optional filters to apply to the request
     * @param {boolean} useCache whether to use the cache or not
     * @param {number} cacheDuration duration of the cache in milliseconds. only used if useCache is true
     * @returns {Observable<T[]>} an observable of the list of items
     */
    drfListPaginatedAllResults<T>(
        route: Routes | string,
        order: string = null,
        filters: DRFFilter = {},
        useCache: boolean = false,
        cacheDuration: number = 1000
    ): Observable<T[]> {
        const params = {
            ...filters,
            ordering: order,
        };
        const key = `${route}${JSON.stringify(params)}`;
        const result = this.drfListPaginated<T>(
            route,
            1,
            100,
            order,
            filters
        ).pipe(concatMap(response => this.drfFollowNext(response)));

        return this.cacheService.cacheWrapper(
            this.cacheNamespace,
            key,
            useCache,
            result,
            cacheDuration
        );
    }

    /**
     * Recursively fetches and concatenates results from a paginated response.
     * @private
     * @param response {PaginatedResponse<T>} the paginated response to fetch results from
     * @returns {Observable<T[]>} an observable of the concatenated results
     */
    private drfFollowNext<T>(response: PaginatedResponse<T>): Observable<T[]> {
        if (response.next) {
            return this.http
                .get<PaginatedResponse<T>>(response.next)
                .pipe(
                    concatMap(nextResponse =>
                        this.drfFollowNext(nextResponse).pipe(
                            map(value => [...response.results, ...value])
                        )
                    )
                );
        } else {
            return of(response.results);
        }
    }

    /**
     * Fetches a single item from the Django REST Framework API.
     * @param route {Routes | string} either route from the Routes enum or a custom string
     * @param id {string} id of the item to fetch
     * @param action {string} optional action to perform on the item, used for building the url {route}/{id}/{action}/
     * @return {Observable<T>} an observable of the item
     */
    drfGet<T>(route: Routes, id: string, action: string = ''): Observable<T> {
        return this.http.get<T>(this._url(route, id, action)).pipe(httpRetry());
    }

    /**
     * Creates an item in the Django REST Framework API.
     * @param route {Routes | string} either route from the Routes enum or a custom string
     * @param item {T} item to create
     * @param resource {string} optional resource to create the item in, used for building the url {route}/{resource}/
     * @param action {string} optional action to perform on the item, used for building the url {route}/{resource}/{action}/
     * @return {Observable<T>} an observable of the created item
     */
    drfCreate<T>(
        route: Routes | string,
        item: T,
        resource: string = '',
        action: string = ''
    ): Observable<T> {
        return this.http.post<T>(this._url(route, resource, action), item);
    }

    /**
     * Updates an item in the Django REST Framework API.
     * @param route {Routes | string} either route from the Routes enum or a custom string
     * @param id {string | number} id of the item to update
     * @param item {Partial<T>} partial item to update
     * @param action {string} optional action to perform on the item, used for building the url {route}/{id}/{action}/
     * @return {Observable<T>} an observable of the updated item
     */
    drfUpdate<T>(
        route: Routes,
        id: string | number,
        item: Partial<T>,
        action: string = ''
    ): Observable<T> {
        return this.http.patch<T>(this._url(route, id, action), item);
    }

    /**
     * Deletes an item in the Django REST Framework API.
     * @param route {Routes | string} either route from the Routes enum or a custom string
     * @param id {string | number} id of the item to delete
     * @return {Observable<T>} an observable of the deleted item
     */
    drfDelete<T>(route: Routes, id: string | number): Observable<T> {
        return this.http.delete<T>(this._url(route, id));
    }

    invoiceStats(): Observable<InvoiceStats> {
        const route = Routes.invoicesStats;
        return this.http
            .get<InvoiceStats>(this._url(route))
            .pipe(httpRetry(5, 1000));
    }

    acquireLock(lock: Lock): Observable<{ has_lock: boolean; user: string }> {
        const route = Routes.locks;
        return this.http
            .post<{ has_lock: boolean; user: string }>(this._url(route), lock)
            .pipe(httpRetry(10, 100));
    }

    releaseLock(lock: Lock) {
        const route = Routes.locks;
        return this.http
            .post(this._url(route, 'release'), lock)
            .pipe(httpRetry(5, 100));
    }

    version() {
        const route = Routes.version;
        return this.http
            .get<Version>(this._url(route))
            .pipe(httpRetry(10, 10 * 1000));
    }

    clear_cache() {
        this.cacheService.clear_namespace(this.cacheNamespace);
    }

    invalidate_route_cache(route: Routes) {
        this.cacheService.invalidate_cache_key(this.cacheNamespace, route);
    }
}
